import CanSafeDeleteDialog from "@/acp/components/CanSafeDeleteDialog.vue"
// Import from ./acp/router/publicRoutes (a side-effect-free leaf module), NOT ./acp/router: the latter binds to the
// browser history via createWebHistory and is also a separate prod chunk. A static import of it here mutates the shop's
// history, and a dynamic import of it splits a circular chunk that crashes the ACP prod build. (CAN-1048 regression)
import { isPublicAcpRoute } from "./acp/router/publicRoutes"
import { reconfigureRoutes as acpReconfigureRoutes } from "./acp/router/reconfigureRoutes"
import { determineApiAddress } from "./vf"
import { defineVfConfig } from "./vf/config/VfConfig"

if (!import.meta.env.VITE_APP) {
    import.meta.env.VITE_APP = location.pathname.startsWith("/acp") ? "acp" : "shop"
}

const acp = import.meta.env.VITE_APP === "acp"
const shop = import.meta.env.VITE_APP === "shop"

/**
 * Routes that are marked as public can be accessed without being authenticated. The app config and global state get
 * requested anyway, but must not trigger a redirect to the login page. So this is a whitelist of endpoints that can be
 * accessed without a login on public routes.
 *
 * Public routes are used for staff actions that are performed on a dedicated device, like tablets that scan QR codes.
 */
function isPublicAcpBootstrapRequest(url?: string) {
    return url === "/_app_config.json" || url === "/global-state"
}

export default defineVfConfig({
    http: {
        requestHandlers: [
            ({ client }) => {
                client.interceptors.request.use(function (config) {
                    config.baseURL = `//${determineApiAddress()}/api/${acp ? "backend" : "shop"}`

                    return config
                })
            },
            shop
                ? ({ client }) => {
                      client.interceptors.response.use(null, error => {
                          // The "login" api url must not cause a reload because a blocked customer (inactivity) returns
                          // a 401 on login. In this case a dialog is opened to explain that the customer is blocked
                          // due to inactivity, and they can unblock themselves. A reload would close that dialog.
                          if (error?.response?.status === 401 && error?.config?.url !== "login") {
                              location.reload()
                          }

                          return Promise.reject(error)
                      })
                  }
                : ({ client }) => {
                      const loginPath = import.meta.env.DEV ? "/acp/login" : "/login"

                      client.interceptors.response.use(null, async error => {
                          if (location.pathname === loginPath) {
                              return Promise.reject(error)
                          }

                          if (
                              error.response?.status === 401 &&
                              !(isPublicAcpRoute() && isPublicAcpBootstrapRequest(error.config?.url))
                          ) {
                              location.href = loginPath
                              return error
                          }

                          return Promise.reject(error)
                      })
                  },
        ],
    },
    reconfigureRoutes(router, newRoutes) {
        if (acp) {
            acpReconfigureRoutes(router, newRoutes)
        }
    },
    safeDeleteDialog: {
        component: () => CanSafeDeleteDialog,
    },
})
